import { useState } from 'react';
import { useTreeStore } from '@/store/useTreeStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useRelationships, useDeleteRelationship } from '@/hooks/useRelationships';
import { useDeleteFamilyMember } from '@/hooks/useFamilyMembers';
import { RelationshipForm } from '@/components/PersonForm/RelationshipForm';
import { FamilyMemberForm } from '@/components/PersonForm/FamilyMemberForm';
import { ElternForm } from '@/components/PersonForm/ElternForm';
import { useConfirm } from '@/components/common/ConfirmDialog';
import type { PersonDetail } from '@/api/persons.api';
import type { Relationship } from '@/api/relationships.api';

// ─── Anklickbarer Personen-Link ─────────────────────────────────────────────

function PersonLink({
  id,
  firstName,
  lastName,
  gender,
}: {
  id: string;
  firstName: string;
  lastName: string | null;
  gender: string | null;
}) {
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);

  return (
    <button
      onClick={() => setSelectedPerson(id)}
      className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-sm font-medium text-blue-600
                 transition-colors hover:bg-blue-50 hover:text-blue-800"
    >
      <span className="text-xs">
        {gender === 'male' ? '♂' : gender === 'female' ? '♀' : '◎'}
      </span>
      {firstName} {lastName ?? ''}
    </button>
  );
}

// ─── Löschen-Button ─────────────────────────────────────────────────────────

function DeleteButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title="Zuordnung entfernen"
      className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-slate-400
                 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
    >
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

// ─── Beziehungstyp-Labels ───────────────────────────────────────────────────

const RELATION_TYPE_LABELS: Record<string, string> = {
  biological: 'Biologisch',
  adopted: 'Adoptiert',
  foster: 'Pflegekind',
  step: 'Stiefkind',
};

const PARTNERSHIP_TYPE_LABELS: Record<string, string> = {
  married: 'Verheiratet',
  partnership: 'Partnerschaft',
  divorced: 'Geschieden',
  separated: 'Getrennt',
};

const PARTNERSHIP_COLORS: Record<string, string> = {
  married: 'bg-blue-100 text-blue-700',
  partnership: 'bg-violet-100 text-violet-700',
  divorced: 'bg-slate-100 text-slate-600',
  separated: 'bg-slate-100 text-slate-600',
};

// ─── RelationshipsTab ───────────────────────────────────────────────────────

interface RelationshipsTabProps {
  person: PersonDetail;
}

export function RelationshipsTab({ person }: RelationshipsTabProps) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const {
    data: relationships,
    isLoading: relsLoading,
    isError: relsError,
  } = useRelationships(person.id);
  const deleteFamilyMember = useDeleteFamilyMember();
  const deleteRelationship = useDeleteRelationship();
  const confirm = useConfirm();
  const [showRelForm, setShowRelForm] = useState(false);
  const [showChildForm, setShowChildForm] = useState(false);
  const [showElternForm, setShowElternForm] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState<Relationship | null>(null);

  // ─── Eltern ─────────────────────────────────────────────────────────────

  const parentEntries = person.familyAsChild ?? [];

  // ─── Kinder ─────────────────────────────────────────────────────────────

  const childEntries = person.children ?? [];

  // ─── Löschen-Handler ─────────────────────────────────────────────────────

  const handleDeleteFamilyMember = async (id: string, label: string) => {
    const ok = await confirm({
      title: 'Zuordnung entfernen?',
      message: (
        <>
          Die Zuordnung <strong>"{label}"</strong> wird entfernt.
        </>
      ),
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (!ok) return;
    deleteFamilyMember.mutate(id);
  };

  const handleDeleteRelationship = async (id: string, partnerName: string) => {
    const ok = await confirm({
      title: 'Partnerschaft entfernen?',
      message: (
        <>
          Die Partnerschaft mit <strong>"{partnerName}"</strong> wird entfernt.
        </>
      ),
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (!ok) return;
    deleteRelationship.mutate(id);
  };

  return (
    <div className="space-y-6">
      {/* ═══ Eltern ═══════════════════════════════════════════════════════════ */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Eltern
          </h4>
          {/* Der Weg rückwärts. Ahnenforschung läuft so: man trägt sich selbst
              ein und sucht dann die Eltern – bisher ging nur der umgekehrte
              Weg über die Eltern-Karteikarte. */}
          {canManage && (
            <button
              onClick={() => setShowElternForm(true)}
              className="rounded px-2 py-0.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
            >
              + Eltern zuordnen
            </button>
          )}
        </div>

        {showElternForm && (
          <ElternForm
            childId={person.id}
            childName={`${person.firstName} ${person.lastName ?? ''}`.trim()}
            existingParentIds={parentEntries.flatMap((e) =>
              [e.parent1?.id, e.parent2?.id].filter((x): x is string => Boolean(x)),
            )}
            bestehende={parentEntries.map((e) => ({
              relationType: e.relationType,
              namen: [e.parent1, e.parent2]
                .filter(Boolean)
                .map((p) => `${p!.firstName} ${p!.lastName ?? ''}`.trim()),
            }))}
            onClose={() => setShowElternForm(false)}
          />
        )}

        {parentEntries.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Eltern zugeordnet.</p>
        ) : (
          <div className="space-y-2">
            {parentEntries.map((entry) => {
              const parentNames = [entry.parent1, entry.parent2]
                .filter(Boolean)
                .map((p) => `${p!.firstName} ${p!.lastName ?? ''}`.trim())
                .join(' & ');

              return (
                <div
                  key={entry.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {entry.parent1 && (
                        <PersonLink {...entry.parent1} />
                      )}
                      {entry.parent1 && entry.parent2 && (
                        <span className="text-xs text-slate-400">&amp;</span>
                      )}
                      {entry.parent2 && (
                        <PersonLink {...entry.parent2} />
                      )}
                    </div>
                    {canManage && (
                      <DeleteButton
                        onClick={() => handleDeleteFamilyMember(entry.id, parentNames)}
                        disabled={deleteFamilyMember.isPending}
                      />
                    )}
                  </div>
                  <span className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-white border border-slate-200">
                    {RELATION_TYPE_LABELS[entry.relationType] ?? entry.relationType}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <hr className="border-slate-200" />

      {/* ═══ Partnerschaften ══════════════════════════════════════════════════ */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Partnerschaften
          </h4>
          {canManage && (
            <button
              onClick={() => setShowRelForm(!showRelForm)}
              className="rounded px-2 py-0.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
            >
              + Hinzufügen
            </button>
          )}
        </div>

        {/* RelationshipForm-Modal (Erstellen) */}
        {showRelForm && (
          <RelationshipForm personId={person.id} onClose={() => setShowRelForm(false)} />
        )}

        {/* RelationshipForm-Modal (Bearbeiten) */}
        {editingRelationship && (
          <RelationshipForm
            personId={person.id}
            existingRelationship={editingRelationship}
            onClose={() => setEditingRelationship(null)}
          />
        )}

        {relsLoading ? (
          <p className="text-sm text-slate-400">Partnerschaften werden geladen…</p>
        ) : relsError ? (
          <p className="text-sm text-red-500">Partnerschaften konnten nicht geladen werden.</p>
        ) : !relationships || relationships.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Partnerschaften vorhanden.</p>
        ) : (
          <div className="space-y-2">
            {relationships.map((rel) => {
              const isP1 = rel.person1.id === person.id;
              const partner = isP1 ? rel.person2 : rel.person1;
              const partnerName = `${partner.firstName} ${partner.lastName ?? ''}`.trim();
              const typeColor = PARTNERSHIP_COLORS[rel.type] ?? PARTNERSHIP_COLORS.separated;

              return (
                <div
                  key={rel.id}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/50 p-2.5"
                >
                  <div className="flex-1">
                    <PersonLink
                      id={partner.id}
                      firstName={partner.firstName}
                      lastName={partner.lastName}
                      gender={null}
                    />
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${typeColor}`}>
                        {PARTNERSHIP_TYPE_LABELS[rel.type] ?? rel.type}
                      </span>
                      {rel.startDate && (
                        <span className="text-[10px] text-slate-400">
                          seit {new Date(rel.startDate).getFullYear()}
                        </span>
                      )}
                      {rel.endDate && (
                        <span className="text-[10px] text-slate-400">
                          bis {new Date(rel.endDate).getFullYear()}
                        </span>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingRelationship(rel)}
                        title="Bearbeiten"
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-slate-400
                                   transition-colors hover:bg-blue-50 hover:text-blue-500"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
                        </svg>
                      </button>
                      <DeleteButton
                        onClick={() => handleDeleteRelationship(rel.id, partnerName)}
                        disabled={deleteRelationship.isPending}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <hr className="border-slate-200" />

      {/* ═══ Kinder ═══════════════════════════════════════════════════════════ */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Kinder
          </h4>
          {canManage && (
            <button
              onClick={() => setShowChildForm(!showChildForm)}
              className="rounded px-2 py-0.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
            >
              + Kind hinzufügen
            </button>
          )}
        </div>

        {/* FamilyMemberForm-Modal */}
        {showChildForm && (
          <FamilyMemberForm
            parentId={person.id}
            coParents={(relationships ?? []).map((rel) => {
              const partner = rel.person1.id === person.id ? rel.person2 : rel.person1;
              return {
                partnerId: partner.id,
                partnerName: `${partner.firstName} ${partner.lastName ?? ''}`.trim(),
                relationshipId: rel.id,
                type: rel.type,
              };
            })}
            existingChildIds={childEntries.map((e) => e.child.id)}
            onClose={() => setShowChildForm(false)}
          />
        )}

        {childEntries.length === 0 ? (
          <p className="text-sm text-slate-400">Keine Kinder zugeordnet.</p>
        ) : (
          <div className="space-y-2">
            {childEntries.map((entry) => {
              const childName = `${entry.child.firstName} ${entry.child.lastName ?? ''}`.trim();
              // Der jeweils andere Elternteil dieses Kind-Eintrags
              const otherParent =
                entry.parent1?.id === person.id ? entry.parent2 : entry.parent1;
              // Ohne zweiten Elternteil startet die Ahnenlinie an der Person
              // statt am Paar-Symbol – nur warnen, wenn Partner existieren
              const missingCoParent = !otherParent && (relationships?.length ?? 0) > 0;

              return (
                <div
                  key={entry.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5"
                >
                  <div className="flex items-center justify-between">
                    <PersonLink
                      id={entry.child.id}
                      firstName={entry.child.firstName}
                      lastName={entry.child.lastName}
                      gender={entry.child.gender}
                    />
                    <div className="flex items-center gap-2">
                      {entry.child.birthDateText && (
                        <span className="text-xs text-slate-400">
                          * {entry.child.birthDateText}
                        </span>
                      )}
                      {canManage && (
                        <DeleteButton
                          onClick={() => handleDeleteFamilyMember(entry.id, childName)}
                          disabled={deleteFamilyMember.isPending}
                        />
                      )}
                    </div>
                  </div>
                  {otherParent && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      mit {otherParent.firstName} {otherParent.lastName ?? ''}
                    </p>
                  )}
                  {missingCoParent && (
                    <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-600">
                      <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                      </svg>
                      Kein zweiter Elternteil zugeordnet – die Linie im Baum startet
                      bei der Person statt beim Paar-Symbol.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
